/**
 * Loans page — list of all loans (we lent + we borrowed) with KPIs, status
 * tabs, a DataTable, and a detail Sheet that shows the amortization schedule
 * + repayment history.
 */

import { useMemo, useState } from "react";

import { createFileRoute } from "@tanstack/react-router";
import {
  Landmark,
  Pencil,
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  Wallet,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneyCompact } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  generateMockSchedule,
  mockLoanRepayments,
  mockLoans,
  type MockLoan,
  type MockLoanRepayment,
  type MockLoanStatus,
} from "../../../lib/financeMock";
import { LoanForm, type LoanFormValues } from "../../../components/forms/LoanForm";
import {
  LoanPaymentForm,
  type LoanPaymentFormValues,
} from "../../../components/forms/LoanPaymentForm";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";

export const Route = createFileRoute("/_app/finance/loans")({
  component: LoansPage,
});

const STATUS_VARIANT: Record<MockLoanStatus, "info" | "success" | "danger" | "neutral"> = {
  active: "info",
  paid_off: "success",
  defaulted: "danger",
  written_off: "neutral",
};
const STATUS_LABEL: Record<MockLoanStatus, string> = {
  active: "Active",
  paid_off: "Paid off",
  defaulted: "Defaulted",
  written_off: "Written off",
};

const KIND_LABEL: Record<string, string> = {
  bank_loan: "Bank loan",
  dealer_credit: "Dealer credit",
  supplier_advance: "Supplier advance",
  staff_advance: "Staff advance",
  other: "Other",
};

function fxRateForCurrency(code: string): bigint {
  if (code === "BDT") return 100_000_000n;
  if (code === "USD") return 11_120_000_000n;
  if (code === "EUR") return 12_080_000_000n;
  if (code === "CNY") return 1_555_000_000n;
  if (code === "GBP") return 14_050_000_000n;
  return 100_000_000n;
}

function LoansPage() {
  const isMock = useMockData();
  void isMock;
  const [rows, setRows] = usePersistentMockState<MockLoan[]>("loans", mockLoans);
  const [repayments, setRepayments] = usePersistentMockState<MockLoanRepayment[]>(
    "loanRepayments",
    mockLoanRepayments,
  );
  const [tab, setTab] = useState<"all" | MockLoanStatus>("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<MockLoan | null>(null);
  const [editingLoan, setEditingLoan] = useState<MockLoan | null>(null);
  const [recordingPaymentFor, setRecordingPaymentFor] =
    useState<MockLoan | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab]);

  // KPIs
  const activeRows = rows.filter((r) => r.status === "active");
  const totalOutstanding = activeRows.reduce(
    (s, r) => s + r.outstandingPrincipalInBaseCents,
    0n,
  );
  const totalPrincipal = activeRows.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const incomingCount = activeRows.filter((r) => r.direction === "incoming").length;
  const outgoingCount = activeRows.filter((r) => r.direction === "outgoing").length;

  const columns = useMemo<ColumnDef<MockLoan>[]>(
    () => [
      {
        accessorKey: "loanNumber",
        header: "Loan #",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.loanNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "direction",
        header: "Dir",
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex h-5 w-5 items-center justify-center rounded-full",
              row.original.direction === "outgoing"
                ? "bg-(--danger-soft) text-(--danger)"
                : "bg-(--success-soft) text-(--success)",
            )}
            title={row.original.direction === "outgoing" ? "We owe" : "Owed to us"}
          >
            {row.original.direction === "outgoing" ? (
              <ArrowDownLeft className="h-3 w-3" />
            ) : (
              <ArrowUpRight className="h-3 w-3" />
            )}
          </span>
        ),
      },
      {
        accessorKey: "counterpartyName",
        header: "Counterparty",
        cell: ({ row }) => (
          <span className="text-(--text-primary)">{row.original.counterpartyName}</span>
        ),
      },
      {
        id: "kind",
        header: "Kind",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm">
            {KIND_LABEL[row.original.loanKind] ?? row.original.loanKind}
          </Badge>
        ),
      },
      {
        id: "principal",
        header: "Principal",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-primary)">
            {formatMoney(row.original.principalCents, row.original.currency, {
              display: row.original.currency === "BDT" ? "symbol" : "code",
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "outstanding",
        header: "Outstanding",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoney(row.original.outstandingPrincipalCents, row.original.currency, {
              display: row.original.currency === "BDT" ? "symbol" : "code",
              locale: "en-BD",
            })}
          </span>
        ),
      },
      {
        id: "rate",
        header: "Rate",
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {(Number(row.original.interestRatePercentE4) / 1e4).toFixed(2)}%
          </span>
        ),
      },
      {
        id: "schedule",
        header: "Schedule",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) capitalize">
            {row.original.scheduleKind} · {row.original.tenorPeriods}m
          </span>
        ),
      },
      {
        accessorKey: "issuedDate",
        header: "Issued",
        cell: ({ row }) => (
          <span className="text-(--text-tertiary) tabular-nums text-[12px]">
            {formatDate(row.original.issuedDate, { style: "short" })}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill variant={STATUS_VARIANT[row.original.status]} size="sm">
            {STATUS_LABEL[row.original.status]}
          </StatusPill>
        ),
      },
    ],
    [],
  );

  function handleCreate(values: LoanFormValues) {
    const issuedMs = new Date(values.issuedDate).getTime();
    const dueMs = new Date(values.dueDate).getTime();
    const fxRate = fxRateForCurrency(values.currency);
    const inBase = (values.principalCents * fxRate) / 100_000_000n;
    const newLoan: MockLoan = {
      _id: `loan_local_${Date.now()}`,
      loanNumber: `LN-${new Date(issuedMs).getFullYear()}-${String(rows.length + 1).padStart(6, "0")}`,
      loanKind: values.loanKind,
      counterpartyName: values.counterpartyName,
      direction: values.direction,
      currency: values.currency,
      principalCents: values.principalCents,
      outstandingPrincipalCents: values.principalCents,
      fxRateToBaseE8: fxRate,
      amountInBaseCents: inBase,
      outstandingPrincipalInBaseCents: inBase,
      interestRatePercentE4: BigInt(
        Math.round(parseFloat(values.interestRatePercent || "0") * 1e4),
      ),
      interestType: values.interestType,
      scheduleKind: values.scheduleKind,
      tenorPeriods: values.tenorPeriods,
      issuedDate: issuedMs,
      dueDate: dueMs,
      status: "active",
      notes: values.notes || null,
    };
    setRows((prev) => [newLoan, ...prev]);
    setCreateOpen(false);
    toast.success(`Loan ${newLoan.loanNumber} booked (mock)`);
  }

  function handleEditLoan(values: LoanFormValues) {
    if (!editingLoan) return;
    if (editingLoan.status !== "active") {
      toast.error("Closed loans cannot be edited.");
      return;
    }
    const issuedMs = new Date(values.issuedDate).getTime();
    const dueMs = new Date(values.dueDate).getTime();
    const fxRate = fxRateForCurrency(values.currency);
    const inBase = (values.principalCents * fxRate) / 100_000_000n;
    const interestRateE4 = BigInt(
      Math.round(parseFloat(values.interestRatePercent || "0") * 1e4),
    );
    setRows((prev) =>
      prev.map((r) =>
        r._id === editingLoan._id
          ? {
              ...r,
              loanKind: values.loanKind,
              counterpartyName: values.counterpartyName,
              direction: values.direction,
              currency: values.currency,
              principalCents: values.principalCents,
              fxRateToBaseE8: fxRate,
              amountInBaseCents: inBase,
              interestRatePercentE4: interestRateE4,
              interestType: values.interestType,
              scheduleKind: values.scheduleKind,
              tenorPeriods: values.tenorPeriods,
              issuedDate: issuedMs,
              dueDate: dueMs,
              notes: values.notes || null,
            }
          : r,
      ),
    );
    setDetail((prev) =>
      prev && prev._id === editingLoan._id
        ? {
            ...prev,
            loanKind: values.loanKind,
            counterpartyName: values.counterpartyName,
            direction: values.direction,
            currency: values.currency,
            principalCents: values.principalCents,
            fxRateToBaseE8: fxRate,
            amountInBaseCents: inBase,
            interestRatePercentE4: interestRateE4,
            interestType: values.interestType,
            scheduleKind: values.scheduleKind,
            tenorPeriods: values.tenorPeriods,
            issuedDate: issuedMs,
            dueDate: dueMs,
            notes: values.notes || null,
          }
        : prev,
    );
    setEditingLoan(null);
    toast.success(`Updated ${editingLoan.loanNumber}. Audit trail recorded.`);
  }

  function handleRecordPayment(values: LoanPaymentFormValues) {
    if (!recordingPaymentFor) return;
    const loan = recordingPaymentFor;
    if (loan.status !== "active") {
      toast.error("Only active loans accept new payments.");
      return;
    }

    // FX-convert principal+interest to base currency. Reuses the loan's
    // booking rate; in production we'd use the rate at the payment date,
    // but for the mock we keep one rate per loan to keep totals tidy.
    const totalCents = values.principalCents + values.interestCents;
    const fxRate = loan.fxRateToBaseE8;
    const totalInBase = (totalCents * fxRate) / 100_000_000n;
    void totalInBase;

    // Build the new MockLoanRepayment.
    const repaymentNumber = `LRP-${new Date().getFullYear()}-${String(repayments.length + 1).padStart(6, "0")}`;
    const newRepayment: MockLoanRepayment = {
      _id: `lrp_local_${Date.now()}`,
      loanId: loan._id,
      repaymentNumber,
      occurredAt: new Date(values.occurredAt).getTime(),
      principalCents: values.principalCents,
      interestCents: values.interestCents,
      totalCents,
      currency: loan.currency,
      paymentMethod: values.paymentMethod,
    };
    setRepayments((prev) => [...prev, newRepayment]);

    // Reduce outstanding principal. If the payment closes the loan,
    // flip status to paid_off (in both the rows list and the local
    // detail so the UI reflects the new state immediately).
    const newOutstanding =
      loan.outstandingPrincipalCents - values.principalCents;
    const clamped = newOutstanding < 0n ? 0n : newOutstanding;
    const newOutstandingBase = (clamped * fxRate) / 100_000_000n;
    const newStatus: MockLoanStatus = clamped === 0n ? "paid_off" : loan.status;

    setRows((prev) =>
      prev.map((r) =>
        r._id === loan._id
          ? {
              ...r,
              outstandingPrincipalCents: clamped,
              outstandingPrincipalInBaseCents: newOutstandingBase,
              status: newStatus,
            }
          : r,
      ),
    );
    setDetail((prev) =>
      prev && prev._id === loan._id
        ? {
            ...prev,
            outstandingPrincipalCents: clamped,
            outstandingPrincipalInBaseCents: newOutstandingBase,
            status: newStatus,
          }
        : prev,
    );

    setRecordingPaymentFor(null);
    if (newStatus === "paid_off") {
      toast.success(
        `${loan.loanNumber} fully repaid. Loan moved to Paid off.`,
      );
    } else {
      toast.success(
        `${repaymentNumber} recorded against ${loan.loanNumber}.`,
      );
    }
  }

  function handleExportCsv(extension: "csv" | "xls") {
    // First export: loan summary rows.
    const loanColumns: ExportColumn<MockLoan>[] = [
      { key: "loanNumber", label: "Loan #" },
      { key: "loanKind", label: "Kind", format: (v) => KIND_LABEL[String(v)] ?? String(v) },
      { key: "counterpartyName", label: "Counterparty" },
      { key: "direction", label: "Direction" },
      { key: "principalCents", label: "Principal" },
      { key: "currency", label: "Currency" },
      { key: "amountInBaseCents", label: "Principal BDT" },
      { key: "outstandingPrincipalCents", label: "Outstanding" },
      { key: "outstandingPrincipalInBaseCents", label: "Outstanding BDT" },
      {
        key: "interestRatePercentE4",
        label: "Interest %",
        format: (v) => (Number(v as bigint) / 10000).toFixed(2),
      },
      { key: "interestType", label: "Interest type" },
      { key: "scheduleKind", label: "Schedule" },
      { key: "tenorPeriods", label: "Tenor" },
      { key: "issuedDate", label: "Issued", format: (v) => formatDateForCsv(Number(v)) },
      { key: "dueDate", label: "Due", format: (v) => formatDateForCsv(v as number | null) },
      { key: "status", label: "Status" },
    ];
    downloadCsv(
      exportFilename("loans", extension),
      toCsv(filtered, loanColumns),
    );
    // Second export: amortization schedule rows for each visible loan.
    type SchedRow = {
      loanNumber: string;
      counterparty: string;
      period: number;
      dueDate: string;
      principalCents: bigint;
      interestCents: bigint;
      paymentCents: bigint;
      balanceCents: bigint;
      currency: string;
      isPaid: boolean;
    };
    const schedRows: SchedRow[] = [];
    for (const loan of filtered) {
      const schedule = generateMockSchedule(loan);
      for (const p of schedule) {
        schedRows.push({
          loanNumber: loan.loanNumber,
          counterparty: loan.counterpartyName,
          period: p.index,
          dueDate: formatDateForCsv(p.dueDate),
          principalCents: p.principalCents,
          interestCents: p.interestCents,
          paymentCents: p.totalCents,
          balanceCents: p.remainingPrincipalCents,
          currency: loan.currency,
          isPaid: p.isPaid,
        });
      }
    }
    const schedColumns: ExportColumn<SchedRow>[] = [
      { key: "loanNumber", label: "Loan #" },
      { key: "counterparty", label: "Counterparty" },
      { key: "period", label: "Period" },
      { key: "dueDate", label: "Due date" },
      { key: "principalCents", label: "Principal" },
      { key: "interestCents", label: "Interest" },
      { key: "paymentCents", label: "Payment" },
      { key: "balanceCents", label: "Remaining principal" },
      { key: "currency", label: "Currency" },
      { key: "isPaid", label: "Paid" },
    ];
    downloadCsv(
      exportFilename("loan-schedules", extension),
      toCsv(schedRows, schedColumns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Loans"
        description="Track money lent out, supplier advances, and bank loans on the books."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New loan
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Loan KPIs"
      >
        <KpiCard
          caption="Active loans"
          value={activeRows.length}
          hint={`${incomingCount} incoming · ${outgoingCount} outgoing`}
        />
        <KpiCard
          caption="Total principal"
          value={formatMoneyCompact(totalPrincipal, "BDT", { locale: "en-BD" })}
          hint="At posting-time FX"
        />
        <KpiCard
          caption="Outstanding"
          value={formatMoneyCompact(totalOutstanding, "BDT", { locale: "en-BD" })}
          hint="Across active loans"
        />
        <KpiCard
          caption="Loan kinds"
          value={Object.keys(
            activeRows.reduce<Record<string, true>>((m, r) => {
              m[r.loanKind] = true;
              return m;
            }, {}),
          ).length}
          hint="Distinct kinds in book"
        />
      </section>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="paid_off">Paid off</TabsTrigger>
          <TabsTrigger value="defaulted">Defaulted</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <DataTable
            data={filtered}
            columns={columns}
            getRowId={(r) => r._id}
            onRowClick={(row) => setDetail(row.original)}
            emptyTitle="No loans"
            emptyDescription="Open a new loan to start tracking obligations."
            emptyIcon={<Landmark className="h-5 w-5" aria-hidden />}
          />
        </TabsContent>
      </Tabs>

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New loan</SheetTitle>
            <SheetDescription>
              FX rate snapshots at posting; the schedule is generated when the loan is activated.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <LoanForm
              fxRateE8={fxRateForCurrency("USD")}
              baseCurrency="BDT"
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Sheet
        open={detail !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDetail(null);
            setEditingLoan(null);
            setRecordingPaymentFor(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detail && editingLoan ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingLoan.loanNumber}</SheetTitle>
                <SheetDescription>
                  Adjust principal, rate, schedule, or counterparty.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <LoanForm
                  submitLabel="Save changes"
                  fxRateE8={fxRateForCurrency(editingLoan.currency)}
                  baseCurrency="BDT"
                  initialValues={{
                    loanKind: editingLoan.loanKind,
                    counterpartyName: editingLoan.counterpartyName,
                    direction: editingLoan.direction,
                    principalCents: editingLoan.principalCents,
                    currency: editingLoan.currency,
                    interestRatePercent: (
                      Number(editingLoan.interestRatePercentE4) / 1e4
                    ).toFixed(2),
                    interestType: editingLoan.interestType,
                    scheduleKind: editingLoan.scheduleKind,
                    tenorPeriods: editingLoan.tenorPeriods,
                    issuedDate: new Date(editingLoan.issuedDate)
                      .toISOString()
                      .slice(0, 10),
                    dueDate: editingLoan.dueDate
                      ? new Date(editingLoan.dueDate)
                          .toISOString()
                          .slice(0, 10)
                      : new Date().toISOString().slice(0, 10),
                    notes: editingLoan.notes ?? "",
                  }}
                  onSubmit={handleEditLoan}
                  onCancel={() => setEditingLoan(null)}
                />
              </SheetBody>
            </>
          ) : detail && recordingPaymentFor ? (
            <>
              <SheetHeader>
                <SheetTitle>
                  Record payment ·{" "}
                  <span className="font-mono">
                    {recordingPaymentFor.loanNumber}
                  </span>
                </SheetTitle>
                <SheetDescription>
                  {recordingPaymentFor.direction === "outgoing"
                    ? "Money paid TO the counterparty."
                    : "Money received FROM the counterparty."}{" "}
                  Outstanding will recompute and the loan will close
                  automatically when the principal hits zero.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                {(() => {
                  // Suggest the next unpaid scheduled period as the default
                  // principal/interest split. Falls back to zero if every
                  // period is already paid.
                  const sched = generateMockSchedule(recordingPaymentFor);
                  const next = sched.find((s) => !s.isPaid);
                  return (
                    <LoanPaymentForm
                      outstandingPrincipalCents={
                        recordingPaymentFor.outstandingPrincipalCents
                      }
                      currency={recordingPaymentFor.currency}
                      suggestedPrincipalCents={next?.principalCents ?? 0n}
                      suggestedInterestCents={next?.interestCents ?? 0n}
                      onSubmit={handleRecordPayment}
                      onCancel={() => setRecordingPaymentFor(null)}
                    />
                  );
                })()}
              </SheetBody>
            </>
          ) : detail ? (
            <LoanDetail
              loan={detail}
              repayments={repayments}
              onEdit={() => setEditingLoan(detail)}
              onRecordPayment={() => setRecordingPaymentFor(detail)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LoanDetail({
  loan,
  repayments,
  onEdit,
  onRecordPayment,
}: {
  loan: MockLoan;
  repayments: MockLoanRepayment[];
  onEdit: () => void;
  onRecordPayment: () => void;
}) {
  const schedule = useMemo(() => generateMockSchedule(loan), [loan]);
  const loanRepayments = useMemo(
    () =>
      repayments
        .filter((r) => r.loanId === loan._id)
        .sort((a, b) => b.occurredAt - a.occurredAt),
    [repayments, loan._id],
  );
  const canEdit = loan.status === "active";
  const canRecordPayment =
    loan.status === "active" && loan.outstandingPrincipalCents > 0n;
  const totalPaidCents = loanRepayments.reduce(
    (s, r) => s + r.totalCents,
    0n,
  );

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{loan.loanNumber}</span>
          <StatusPill variant={STATUS_VARIANT[loan.status]} size="sm">
            {STATUS_LABEL[loan.status]}
          </StatusPill>
          <RecordAuditBadge tableName="loans" recordId={loan._id} />
        </SheetTitle>
        <SheetDescription>
          {loan.counterpartyName} · {KIND_LABEL[loan.loanKind] ?? loan.loanKind}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Principal"
              value={formatMoney(loan.principalCents, loan.currency, {
                display: loan.currency === "BDT" ? "symbol" : "code",
                locale: "en-BD",
              })}
            />
            <DetailField
              label="In BDT"
              value={formatMoney(loan.amountInBaseCents, "BDT", { locale: "en-BD" })}
            />
            <DetailField
              label="Outstanding"
              value={formatMoney(loan.outstandingPrincipalCents, loan.currency, {
                display: loan.currency === "BDT" ? "symbol" : "code",
                locale: "en-BD",
              })}
            />
            <DetailField
              label="Rate"
              value={`${(Number(loan.interestRatePercentE4) / 1e4).toFixed(2)}% ${loan.interestType}`}
            />
            <DetailField
              label="Issued"
              value={formatDate(loan.issuedDate, { style: "medium" })}
            />
            <DetailField
              label="Maturity"
              value={loan.dueDate ? formatDate(loan.dueDate, { style: "medium" }) : "—"}
            />
            <DetailField
              label="Schedule"
              value={`${loan.scheduleKind} · ${loan.tenorPeriods} months`}
            />
            <DetailField
              label="Direction"
              value={loan.direction === "outgoing" ? "We owe" : "Owed to us"}
            />
          </section>

          {loan.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {loan.notes}
              </p>
            </section>
          ) : null}

          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Amortization schedule
            </div>
            <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) overflow-hidden">
              <table className="w-full text-[12px] table-fixed">
                <colgroup>
                  <col className="w-12" />
                  <col className="w-24" />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-(--text-tertiary) border-b border-(--border-subtle) bg-(--bg-sunken)">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Due</th>
                    <th className="px-3 py-2 text-right">Principal</th>
                    <th className="px-3 py-2 text-right">Interest</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.slice(0, 24).map((row) => (
                    <tr
                      key={row.index}
                      className={cn(
                        "border-b border-(--border-subtle) last:border-0",
                        row.isPaid && "bg-(--success-soft)/30",
                      )}
                    >
                      <td className="px-3 py-1.5 font-mono text-(--text-tertiary) tabular-nums">
                        {row.index}
                      </td>
                      <td className="px-3 py-1.5 text-(--text-secondary) tabular-nums">
                        {formatDate(row.dueDate, { style: "short" })}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-(--text-primary)">
                        {formatMoney(row.principalCents, loan.currency, {
                          display: "none",
                          locale: "en-BD",
                        })}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-(--text-secondary)">
                        {formatMoney(row.interestCents, loan.currency, {
                          display: "none",
                          locale: "en-BD",
                        })}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-(--text-primary) font-medium">
                        {formatMoney(row.totalCents, loan.currency, {
                          display: "none",
                          locale: "en-BD",
                        })}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-(--text-tertiary)">
                        {formatMoney(row.remainingPrincipalCents, loan.currency, {
                          display: "none",
                          locale: "en-BD",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {schedule.length > 24 ? (
                <div className="px-3 py-2 text-[11px] text-(--text-tertiary) border-t border-(--border-subtle)">
                  Showing 24 of {schedule.length} periods
                </div>
              ) : null}
            </div>
          </section>

          <section>
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                Repayments ({loanRepayments.length})
              </div>
              {totalPaidCents > 0n ? (
                <div className="text-[11px] tabular-nums text-(--text-tertiary)">
                  Total paid:{" "}
                  <span className="font-mono text-(--text-secondary)">
                    {formatMoney(totalPaidCents, loan.currency, {
                      display: loan.currency === "BDT" ? "symbol" : "code",
                      locale: "en-BD",
                    })}
                  </span>
                </div>
              ) : null}
            </div>
            {loanRepayments.length === 0 ? (
              <div className="rounded-md border border-dashed border-(--border-subtle) bg-(--bg-canvas) px-3 py-4 text-[12px] text-(--text-tertiary) text-center">
                No repayments recorded yet.
                {canRecordPayment ? (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="text-(--accent) hover:underline underline-offset-2"
                      onClick={onRecordPayment}
                    >
                      Record the first one
                    </button>
                    .
                  </>
                ) : null}
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {loanRepayments.map((r) => (
                  <li
                    key={r._id}
                    className="grid grid-cols-[110px_1fr_auto_auto] gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2 text-[12px]"
                  >
                    <span className="text-(--text-tertiary) tabular-nums">
                      {formatDate(r.occurredAt, { style: "short" })}
                    </span>
                    <span className="font-mono text-[11px] text-(--text-tertiary)">
                      {r.repaymentNumber}
                    </span>
                    <span className="text-(--text-secondary) capitalize text-right">
                      {r.paymentMethod.replace(/_/g, " ")}
                    </span>
                    <span className="font-mono tabular-nums text-(--text-primary) text-right">
                      {formatMoney(r.totalCents, r.currency, {
                        display: r.currency === "BDT" ? "symbol" : "code",
                        locale: "en-BD",
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="loans" recordId={loan._id} />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            {canEdit ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={onEdit}
                aria-label="Edit"
              >
                <Pencil className="size-4" />
              </Button>
            ) : (
              <Tooltip content="Cannot edit closed records — create a credit note instead">
                <span>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled
                    aria-label="Edit"
                  >
                    <Pencil className="size-4" />
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
          {canRecordPayment ? (
            <Button onClick={onRecordPayment}>
              <Wallet className="h-4 w-4" /> Record payment
            </Button>
          ) : loan.status === "paid_off" ? (
            <span className="text-[12px] text-(--text-tertiary)">
              Loan fully repaid · no further payments needed
            </span>
          ) : null}
        </div>
      </SheetFooter>
    </>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
